import { z } from 'zod';

export const roleSchema = z.enum(['STAFF', 'MANAGER', 'ADMIN']);
export type Role = z.infer<typeof roleSchema>;

export const connectivitySchema = z.enum(['ONLINE', 'OFFLINE', 'SYNCING']);
export type Connectivity = z.infer<typeof connectivitySchema>;

export const productTypeSchema = z.enum(['PACKAGED', 'PREPARED']);
export const productUnitSchema = z.enum(['EACH', 'G', 'KG', 'ML', 'L']);
export const paymentMethodSchema = z.enum(['CASH', 'CARD', 'RESIDENT_TAB']);
export const externalPaymentMethodSchema = z.enum(['CASH', 'CARD', 'OTHER']);
export const saleStatusSchema = z.enum(['COMPLETED', 'VOIDED', 'PARTIALLY_RETURNED', 'RETURNED']);
export const residentStatusSchema = z.enum(['ACTIVE', 'LIMIT_REACHED', 'PAYMENT_DUE', 'INACTIVE']);
export const correctionReasonSchema = z.enum([
  'WRONG_ITEM',
  'DEFECTIVE',
  'DAMAGED',
  'QUALITY_ISSUE',
  'CUSTOMER_REQUEST',
  'TRANSACTION_ERROR',
  'OTHER',
]);
export const externalRefundStatusSchema = z.enum(['REQUIRED', 'RECORDED_COMPLETE']);
export const correctionKindSchema = z.enum(['VOID', 'RETURN']);

export const stockMovementTypeSchema = z.enum([
  'OPENING',
  'RECEIVE',
  'PREPARED_ADD',
  'SALE',
  'SALE_REVERSAL',
  'RETURN_IN',
  'RETURN_NO_STOCK',
  'WASTE',
  'COMPLIMENTARY',
  'STOCKTAKE_ADJUSTMENT',
  'ADMIN_CORRECTION',
]);
export type StockMovementType = z.infer<typeof stockMovementTypeSchema>;

export const categorySchema = z.object({
  id: z.uuid(),
  name: z.string().trim().min(1).max(80),
  iconKey: z.string().trim().max(40).nullable(),
  sortOrder: z.number().int(),
  active: z.boolean(),
  updatedAt: z.iso.datetime({ offset: true }),
});
export type Category = z.infer<typeof categorySchema>;

export const productSchema = z.object({
  id: z.uuid(),
  name: z.string().trim().min(1).max(160),
  sku: z.string().trim().min(1).max(80),
  barcode: z.string().trim().max(80).nullable(),
  categoryId: z.uuid(),
  productType: productTypeSchema,
  unit: productUnitSchema,
  sellingPriceCents: z.number().int().nonnegative(),
  defaultCostCents: z.number().int().nonnegative().nullable(),
  stockTracked: z.boolean(),
  lowStockThreshold: z.number().nonnegative(),
  imagePath: z.string().trim().max(500).nullable(),
  active: z.boolean(),
  version: z.number().int().positive(),
  updatedAt: z.iso.datetime({ offset: true }),
});
export type Product = z.infer<typeof productSchema>;

export const saleLineSnapshotSchema = z.object({
  productId: z.uuid(),
  productName: z.string().trim().min(1),
  unit: productUnitSchema,
  unitPriceCents: z.number().int().nonnegative(),
  costSnapshotCents: z.number().int().nonnegative().nullable(),
  quantity: z.number().positive(),
  lineTotalCents: z.number().int().nonnegative(),
});
export type SaleLineSnapshot = z.infer<typeof saleLineSnapshotSchema>;

export const mutationEnvelopeSchema = z.object({
  mutationId: z.uuid(),
  deviceId: z.uuid(),
  occurredAtDevice: z.iso.datetime({ offset: true }),
  schemaVersion: z.number().int().positive(),
});
export type MutationEnvelope = z.infer<typeof mutationEnvelopeSchema>;

export const tabDueRuleSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('LAST_CALENDAR_DAY_OF_MONTH') }),
]);
export type TabDueRule = z.infer<typeof tabDueRuleSchema>;

export const operationalPolicySchema = z.object({
  defaultTabLimitCents: z.number().int().positive().default(20_000),
  tabDueRule: tabDueRuleSchema.default({ kind: 'LAST_CALENDAR_DAY_OF_MONTH' }),
  offlineManagerValidationHours: z.number().positive().default(24),
  staleDeviceThresholdMinutes: z.number().positive().default(30),
  voidWindowMinutes: z.number().int().min(1).max(1440).default(30),
  pricesAreTaxInclusive: z.literal(true).default(true),
});
export type OperationalPolicy = z.infer<typeof operationalPolicySchema>;

export const DEFAULT_OPERATIONAL_POLICY: OperationalPolicy = operationalPolicySchema.parse({});

export const ALL_PRODUCTS_VIEW_ID = 'all-products' as const;

export const adminCapabilitySchema = z.enum([
  'VIEW_OPERATIONS',
  'MANAGE_STAFF',
  'MANAGE_DEVICES',
  'MANAGE_SETTINGS',
]);

export const adminIdentitySchema = z.object({
  userId: z.uuid(),
  displayName: z.string().trim().min(1),
  role: z.enum(['MANAGER', 'ADMIN']),
});
export type AdminIdentity = z.infer<typeof adminIdentitySchema>;

export const adminSessionContextSchema = adminIdentitySchema.extend({
  capabilities: z.array(adminCapabilitySchema),
});
export type AdminSessionContext = z.infer<typeof adminSessionContextSchema>;

export const adminExceptionSeveritySchema = z.enum(['CRITICAL', 'ACTION_NEEDED', 'WARNING']);
export type AdminExceptionSeverity = z.infer<typeof adminExceptionSeveritySchema>;
export const adminInventoryStatusSchema = z.enum(['RECONCILE', 'OUT', 'LOW']);
export const adminDeviceHealthStatusSchema = z.enum(['REVOKED', 'UNKNOWN', 'STALE', 'HEALTHY']);

export const adminOverviewSchema = z.object({
  identity: adminIdentitySchema,
  businessDate: z.iso.date(),
  businessTimezone: z.string().min(1),
  dayStart: z.iso.datetime({ offset: true }),
  dayEnd: z.iso.datetime({ offset: true }),
  generatedAt: z.iso.datetime({ offset: true }),
  staleThresholdMinutes: z.number().int().positive(),
  freshness: z.object({
    complete: z.boolean(),
    unhealthyDeviceCount: z.number().int().nonnegative(),
    message: z.string().min(1),
  }),
  metrics: z.object({
    recordedSalesCents: z.number().int(),
    transactionCount: z.number().int().nonnegative(),
    correctionAmountCents: z.number().int().nonnegative(),
    netAfterCorrectionsCents: z.number().int(),
    outstandingTabCents: z.number().int().nonnegative(),
    lowStockCount: z.number().int().nonnegative(),
    outOfStockCount: z.number().int().nonnegative(),
    reconcileCount: z.number().int().nonnegative(),
  }),
  paymentMix: z.object({
    CASH: z.number().int().nonnegative(),
    CARD: z.number().int().nonnegative(),
    RESIDENT_TAB: z.number().int().nonnegative(),
  }),
  salesByHour: z
    .array(
      z.object({
        hour: z.number().int().min(0).max(23),
        recordedSalesCents: z.number().int().nonnegative(),
        transactions: z.number().int().nonnegative(),
      }),
    )
    .length(24),
  topSellers: z.array(
    z.object({
      productName: z.string().min(1),
      unitsSold: z.coerce.number().positive(),
      recordedSalesCents: z.number().int().nonnegative(),
    }),
  ),
  inventoryAttention: z.array(
    z.object({
      productId: z.uuid(),
      productName: z.string().min(1),
      quantityOnHand: z.coerce.number(),
      lowStockThreshold: z.coerce.number().nonnegative(),
      status: adminInventoryStatusSchema,
      sortRank: z.number().int().min(1).max(3),
    }),
  ),
  residentTabAttention: z.array(
    z.object({
      residentId: z.uuid(),
      displayName: z.string().min(1),
      roomReference: z.string().nullable(),
      balanceCents: z.number().int().nonnegative(),
      dueAt: z.iso.datetime({ offset: true }).nullable(),
      status: z.literal('PAYMENT_DUE'),
    }),
  ),
  deviceHealth: z.array(
    z.object({
      deviceId: z.uuid(),
      deviceName: z.string().min(1),
      active: z.boolean(),
      revokedAt: z.iso.datetime({ offset: true }).nullable(),
      lastSyncAt: z.iso.datetime({ offset: true }).nullable(),
      lastSeenAt: z.iso.datetime({ offset: true }).nullable(),
      appVersion: z.string().nullable(),
      protocolVersion: z.number().int().positive(),
      lastReportedPendingCount: z.number().int().nonnegative().nullable(),
      healthStatus: adminDeviceHealthStatusSchema,
      sortRank: z.number().int().min(1).max(4),
    }),
  ),
  exceptions: z.array(
    z.object({
      id: z.string().min(1),
      type: z.string().min(1),
      severity: adminExceptionSeveritySchema,
      title: z.string().min(1),
      description: z.string().min(1),
      entityId: z.string().min(1),
      occurredAt: z.iso.datetime({ offset: true }),
      navigationTarget: z.string().startsWith('/'),
    }),
  ),
});
export type AdminOverview = z.infer<typeof adminOverviewSchema>;

export const adminFreshnessSchema = z.object({
  complete: z.boolean(),
  unhealthyDeviceCount: z.number().int().nonnegative(),
  registeredDeviceCount: z.number().int().nonnegative(),
  staleThresholdMinutes: z.number().int().positive(),
  message: z.string().min(1),
});
export type AdminFreshness = z.infer<typeof adminFreshnessSchema>;

export const adminSaleStatusSchema = z.enum([
  'COMPLETED',
  'PARTIALLY_RETURNED',
  'RETURNED',
  'VOIDED',
]);
export type AdminSaleStatus = z.infer<typeof adminSaleStatusSchema>;

export const adminSalesCursorSchema = z.object({
  createdAt: z.iso.datetime({ offset: true }),
  id: z.uuid(),
});
export type AdminSalesCursor = z.infer<typeof adminSalesCursorSchema>;

export const adminSalesFiltersSchema = z.object({
  search: z.string().trim().max(160).optional(),
  dateFrom: z.iso.date().optional(),
  dateTo: z.iso.date().optional(),
  paymentMethod: paymentMethodSchema.optional(),
  status: z
    .enum(['COMPLETED', 'PARTIALLY_RETURNED', 'RETURNED', 'VOIDED', 'REFUND_REQUIRED'])
    .optional(),
  staffId: z.uuid().optional(),
  deviceId: z.uuid().optional(),
  cursor: adminSalesCursorSchema.optional(),
  limit: z.number().int().min(1).max(100).default(30),
});
export type AdminSalesFilters = z.input<typeof adminSalesFiltersSchema>;

export const adminSalesPageSchema = z.object({
  businessTimezone: z.string().min(1),
  dateFrom: z.iso.date(),
  dateTo: z.iso.date(),
  generatedAt: z.iso.datetime({ offset: true }),
  freshness: adminFreshnessSchema,
  summary: z.object({
    recordedSalesCents: z.number().int().nonnegative(),
    transactions: z.number().int().nonnegative(),
    returnCorrectionCents: z.number().int().nonnegative(),
    voidCorrectionCents: z.number().int().nonnegative(),
    correctionCents: z.number().int().nonnegative(),
    netAfterCorrectionsCents: z.number().int(),
    averageTransactionCents: z.number().int().nonnegative(),
  }),
  items: z.array(
    z.object({
      id: z.uuid(),
      reference: z.string().min(1),
      createdAt: z.iso.datetime({ offset: true }),
      occurredAt: z.iso.datetime({ offset: true }),
      paymentMethod: paymentMethodSchema,
      staffReference: z.uuid().nullable(),
      deviceId: z.uuid(),
      deviceName: z.string().min(1),
      itemCount: z.coerce.number().nonnegative(),
      originalTotalCents: z.number().int().nonnegative(),
      correctionCents: z.number().int().nonnegative(),
      netRetainedCents: z.number().int(),
      status: adminSaleStatusSchema,
      refundRequired: z.boolean(),
    }),
  ),
  nextCursor: adminSalesCursorSchema.nullable(),
});
export type AdminSalesPage = z.infer<typeof adminSalesPageSchema>;

export const adminSalesFilterOptionsSchema = z.object({
  devices: z.array(z.object({ id: z.uuid(), name: z.string().min(1) })),
  staff: z.array(z.object({ id: z.uuid(), label: z.string().min(1) })),
});
export type AdminSalesFilterOptions = z.infer<typeof adminSalesFilterOptionsSchema>;

const adminExternalRefundSchema = z.object({
  id: z.uuid(),
  amountCents: z.number().int().positive(),
  paymentMethod: externalPaymentMethodSchema,
  status: externalRefundStatusSchema,
  recordedCompleteAt: z.iso.datetime({ offset: true }).nullable(),
  externalReference: z.string().nullable(),
});

export const adminSaleDetailSchema = z.object({
  id: z.uuid(),
  reference: z.string().min(1),
  createdAt: z.iso.datetime({ offset: true }),
  occurredAt: z.iso.datetime({ offset: true }),
  paymentMethod: paymentMethodSchema,
  staffReference: z.uuid().nullable(),
  deviceId: z.uuid(),
  deviceName: z.string().min(1),
  originalTotalCents: z.number().int().nonnegative(),
  correctionCents: z.number().int().nonnegative(),
  netRetainedCents: z.number().int(),
  status: adminSaleStatusSchema,
  lines: z.array(
    z.object({
      id: z.uuid(),
      productId: z.uuid(),
      productName: z.string().min(1),
      unit: productUnitSchema,
      unitPriceCents: z.number().int().nonnegative(),
      costCents: z.number().int().nonnegative().nullable(),
      quantity: z.coerce.number().positive(),
      lineTotalCents: z.number().int().nonnegative(),
      returnedQuantity: z.coerce.number().nonnegative(),
    }),
  ),
  corrections: z.array(
    z.object({
      id: z.uuid(),
      kind: z.enum(['VOID', 'RETURN']),
      occurredAt: z.iso.datetime({ offset: true }),
      amountCents: z.number().int().nonnegative(),
      reasonCode: z.string().nullable(),
      note: z.string().nullable(),
      authorizedBy: z.uuid(),
      tabAdjustmentCents: z.number().int().nonnegative(),
      externalRefundCents: z.number().int().nonnegative(),
      lines: z.array(
        z.object({
          productId: z.uuid(),
          quantity: z.coerce.number().positive(),
          amountCents: z.number().int().nonnegative(),
          returnToStock: z.boolean(),
          movementType: stockMovementTypeSchema.nullable(),
        }),
      ),
      externalRefund: adminExternalRefundSchema.nullable(),
    }),
  ),
  residentTab: z
    .object({
      residentId: z.uuid(),
      originalChargeCents: z.number().int().nonnegative(),
      correctionCents: z.number().int().nonnegative(),
      outstandingObligationCents: z.number().int().nonnegative(),
    })
    .nullable(),
});
export type AdminSaleDetail = z.infer<typeof adminSaleDetailSchema>;

export const adminStockStatusSchema = z.enum(['IN_STOCK', 'LOW', 'OUT', 'RECONCILE']);
export type AdminStockStatus = z.infer<typeof adminStockStatusSchema>;

export const adminCategorySchema = z.object({
  id: z.uuid(),
  name: z.string().trim().min(1).max(80),
  iconKey: z.string().nullable(),
  sortOrder: z.number().int(),
  active: z.boolean(),
});
export type AdminCategory = z.infer<typeof adminCategorySchema>;

export const adminInventoryCursorSchema = z.object({ name: z.string().min(1), id: z.uuid() });
export const adminInventoryFiltersSchema = z.object({
  search: z.string().trim().max(160).optional(),
  categoryId: z.uuid().optional(),
  productType: productTypeSchema.optional(),
  stockStatus: adminStockStatusSchema.optional(),
  active: z.enum(['ACTIVE', 'ARCHIVED', 'ALL']).default('ACTIVE'),
  cursor: adminInventoryCursorSchema.optional(),
  limit: z.number().int().min(1).max(100).default(30),
});
export type AdminInventoryFilters = z.input<typeof adminInventoryFiltersSchema>;

export const adminInventoryPageSchema = z.object({
  generatedAt: z.iso.datetime({ offset: true }),
  freshness: adminFreshnessSchema,
  items: z.array(
    z.object({
      id: z.uuid(),
      name: z.string().min(1),
      sku: z.string().min(1),
      barcode: z.string().nullable(),
      categoryId: z.uuid(),
      categoryName: z.string().min(1),
      productType: productTypeSchema,
      sellingPriceCents: z.number().int().nonnegative(),
      costCents: z.number().int().nonnegative().nullable(),
      quantityOnHand: z.coerce.number(),
      lowStockThreshold: z.coerce.number().nonnegative(),
      stockStatus: adminStockStatusSchema,
      active: z.boolean(),
      lastMovementAt: z.iso.datetime({ offset: true }).nullable(),
      lastMovementType: stockMovementTypeSchema.nullable(),
      ledgerMatchesProjection: z.boolean(),
    }),
  ),
  nextCursor: adminInventoryCursorSchema.nullable(),
  categories: z.array(adminCategorySchema),
});
export type AdminInventoryPage = z.infer<typeof adminInventoryPageSchema>;

export const adminMovementCursorSchema = adminSalesCursorSchema;
export const adminProductDetailSchema = z.object({
  product: z.object({
    id: z.uuid(),
    name: z.string().min(1),
    sku: z.string().min(1),
    barcode: z.string().nullable(),
    categoryId: z.uuid(),
    categoryName: z.string().min(1),
    productType: productTypeSchema,
    unit: productUnitSchema,
    sellingPriceCents: z.number().int().nonnegative(),
    costCents: z.number().int().nonnegative().nullable(),
    quantityOnHand: z.coerce.number(),
    lowStockThreshold: z.coerce.number().nonnegative(),
    stockStatus: adminStockStatusSchema,
    active: z.boolean(),
    version: z.number().int().positive(),
    ledgerQuantity: z.coerce.number(),
    ledgerMatchesProjection: z.boolean(),
  }),
  movements: z.array(
    z.object({
      id: z.uuid(),
      createdAt: z.iso.datetime({ offset: true }),
      occurredAt: z.iso.datetime({ offset: true }),
      type: stockMovementTypeSchema,
      quantityDelta: z.coerce.number(),
      resultingBalance: z.coerce.number(),
      actorName: z.string().nullable(),
      staffReference: z.uuid().nullable(),
      deviceName: z.string().nullable(),
      saleReference: z.string().nullable(),
      stocktakeReference: z.string().nullable(),
      returnId: z.uuid().nullable(),
      reasonCode: z.string().nullable(),
      note: z.string().nullable(),
      reference: z.string().nullable(),
      origin: z.enum(['KIOSK', 'ADMIN_WEB']),
    }),
  ),
  nextCursor: adminMovementCursorSchema.nullable(),
  exceptions: z.array(
    z.object({
      id: z.uuid(),
      type: z.string().min(1),
      status: z.enum(['OPEN', 'RESOLVED']),
      details: z.record(z.string(), z.unknown()),
      createdAt: z.iso.datetime({ offset: true }),
      resolvedAt: z.iso.datetime({ offset: true }).nullable(),
    }),
  ),
  stocktakes: z.array(
    z.object({
      id: z.uuid(),
      status: z.enum(['DRAFT', 'AWAITING_SYNC', 'NEEDS_REVIEW', 'COMPLETED']),
      reference: z.string().nullable(),
      startedAt: z.iso.datetime({ offset: true }),
      completedAt: z.iso.datetime({ offset: true }).nullable(),
      expectedQuantity: z.coerce.number(),
      countedQuantity: z.coerce.number().nullable(),
      variance: z.coerce.number().nullable(),
    }),
  ),
  freshness: adminFreshnessSchema,
});
export type AdminProductDetail = z.infer<typeof adminProductDetailSchema>;

export const adminProductInputSchema = z.object({
  mutationId: z.uuid(),
  productId: z.uuid(),
  name: z.string().trim().min(1).max(160),
  sku: z.string().trim().min(1).max(80),
  barcode: z.string().trim().max(80).nullable(),
  categoryId: z.uuid(),
  productType: productTypeSchema,
  sellingPriceCents: z.number().int().nonnegative(),
  costCents: z.number().int().nonnegative().nullable(),
  lowStockThreshold: z.number().nonnegative(),
  active: z.boolean(),
});
export type AdminProductInput = z.infer<typeof adminProductInputSchema>;

export const adminCategoryInputSchema = adminCategorySchema.extend({ mutationId: z.uuid() });
export type AdminCategoryInput = z.infer<typeof adminCategoryInputSchema>;

export const adminStockOperationInputSchema = z.object({
  mutationId: z.uuid(),
  productId: z.uuid(),
  movementType: z.enum(['RECEIVE', 'PREPARED_ADD', 'WASTE', 'COMPLIMENTARY']),
  quantity: z.number().int().positive(),
  reasonCode: z.string().trim().max(80).nullable(),
  note: z.string().trim().max(500).nullable(),
  reference: z.string().trim().max(120).nullable(),
});
export type AdminStockOperationInput = z.infer<typeof adminStockOperationInputSchema>;

export const adminMutationResultSchema = z.object({
  status: z.enum(['ACCEPTED', 'REJECTED', 'NOT_FOUND']),
  mutationId: z.uuid(),
  duplicate: z.boolean().optional(),
  code: z.string().optional(),
  productId: z.uuid().optional(),
  categoryId: z.uuid().optional(),
  quantityOnHand: z.coerce.number().optional(),
  quantityDelta: z.coerce.number().optional(),
  active: z.boolean().optional(),
  created: z.boolean().optional(),
  version: z.number().int().positive().optional(),
  movementType: z.string().optional(),
});
export type AdminMutationResult = z.infer<typeof adminMutationResultSchema>;

export const adminResidentCursorSchema = z.object({ name: z.string().min(1), id: z.uuid() });
export const adminResidentFiltersSchema = z.object({
  search: z.string().trim().max(160).optional(),
  status: residentStatusSchema.optional(),
  active: z.enum(['ACTIVE', 'ARCHIVED', 'ALL']).default('ACTIVE'),
  outstanding: z.boolean().default(false),
  nearLimit: z.boolean().default(false),
  cursor: adminResidentCursorSchema.optional(),
  limit: z.number().int().min(1).max(100).default(30),
});
export type AdminResidentFilters = z.input<typeof adminResidentFiltersSchema>;

export const adminResidentSummarySchema = z.object({
  id: z.uuid(),
  displayName: z.string().min(1),
  roomReference: z.string().nullable(),
  active: z.boolean(),
  creditLimitCents: z.number().int().positive(),
  balanceCents: z.number().int().nonnegative(),
  availableCreditCents: z.number().int().nonnegative(),
  status: residentStatusSchema,
  paymentDue: z.boolean(),
  dueAt: z.iso.datetime({ offset: true }).nullable(),
  nearLimit: z.boolean().optional(),
  createdAt: z.iso.datetime({ offset: true }).optional(),
  updatedAt: z.iso.datetime({ offset: true }),
});
export type AdminResidentSummary = z.infer<typeof adminResidentSummarySchema>;

export const adminResidentsPageSchema = z.object({
  items: z.array(adminResidentSummarySchema),
  nextCursor: adminResidentCursorSchema.nullable(),
  defaultCreditLimitCents: z.number().int().positive(),
  dueRule: tabDueRuleSchema,
  freshness: adminFreshnessSchema,
  generatedAt: z.iso.datetime({ offset: true }),
});
export type AdminResidentsPage = z.infer<typeof adminResidentsPageSchema>;

export const adminResidentLedgerCursorSchema = adminSalesCursorSchema;
export const adminResidentDetailSchema = z.object({
  resident: adminResidentSummarySchema.omit({ nearLimit: true }),
  ledger: z.array(
    z.object({
      id: z.uuid(),
      createdAt: z.iso.datetime({ offset: true }),
      occurredAt: z.iso.datetime({ offset: true }),
      type: z.enum(['CHARGE', 'SETTLEMENT', 'VOID_ADJUSTMENT', 'RETURN_ADJUSTMENT']),
      amountCents: z.number().int(),
      saleId: z.uuid().nullable(),
      saleReference: z.string().nullable(),
      settlementId: z.uuid().nullable(),
      settlementMethod: externalPaymentMethodSchema.nullable(),
      settlementReference: z.string().nullable(),
      settlementNote: z.string().nullable(),
      returnId: z.uuid().nullable(),
      reversalOfEntryId: z.uuid().nullable(),
      actorName: z.string().nullable(),
      staffReference: z.uuid().nullable(),
      deviceName: z.string().nullable(),
      mutationId: z.uuid(),
    }),
  ),
  nextCursor: adminResidentLedgerCursorSchema.nullable(),
  externalRefunds: z.array(
    z.object({
      id: z.uuid(),
      saleReference: z.string().min(1),
      amountCents: z.number().int().positive(),
      method: externalPaymentMethodSchema,
      status: externalRefundStatusSchema,
      requiredAt: z.iso.datetime({ offset: true }),
      completedAt: z.iso.datetime({ offset: true }).nullable(),
      externalReference: z.string().nullable(),
    }),
  ),
  dueRule: tabDueRuleSchema,
  businessTimezone: z.string().min(1),
  freshness: adminFreshnessSchema,
});
export type AdminResidentDetail = z.infer<typeof adminResidentDetailSchema>;

export const adminResidentInputSchema = z.object({
  mutationId: z.uuid(),
  residentId: z.uuid(),
  displayName: z.string().trim().min(1).max(120),
  roomReference: z.string().trim().max(120).nullable(),
  creditLimitCents: z.number().int().positive(),
  active: z.boolean(),
});
export type AdminResidentInput = z.infer<typeof adminResidentInputSchema>;

export const adminStaffCursorSchema = adminResidentCursorSchema;
export const adminStaffFiltersSchema = z.object({
  search: z.string().trim().max(160).optional(),
  role: roleSchema.optional(),
  active: z.enum(['ACTIVE', 'INACTIVE', 'ALL']).default('ACTIVE'),
  cursor: adminStaffCursorSchema.optional(),
  limit: z.number().int().min(1).max(100).default(30),
});
export type AdminStaffFilters = z.input<typeof adminStaffFiltersSchema>;

const adminWebAccountSchema = z
  .object({
    email: z.email(),
    lastSignInAt: z.iso.datetime({ offset: true }).nullable(),
    disabled: z.boolean(),
  })
  .nullable();
export const adminStaffSummarySchema = z.object({
  id: z.uuid(),
  displayName: z.string().min(1),
  role: roleSchema,
  active: z.boolean(),
  pinConfigured: z.boolean(),
  credentialVersion: z.number().int().positive().nullable(),
  pinUpdatedAt: z.iso.datetime({ offset: true }).nullable(),
  webAccount: adminWebAccountSchema,
  hasHistoricalSales: z.boolean().optional(),
  createdAt: z.iso.datetime({ offset: true }),
  updatedAt: z.iso.datetime({ offset: true }),
});
export const adminStaffPageSchema = z.object({
  items: z.array(adminStaffSummarySchema),
  nextCursor: adminStaffCursorSchema.nullable(),
  generatedAt: z.iso.datetime({ offset: true }),
});
export type AdminStaffPage = z.infer<typeof adminStaffPageSchema>;
export const adminStaffDetailSchema = adminStaffSummarySchema
  .omit({ hasHistoricalSales: true })
  .extend({
    historicalAttribution: z.object({
      sales: z.number().int().nonnegative(),
      movements: z.number().int().nonnegative(),
      tabEntries: z.number().int().nonnegative(),
    }),
  });
export type AdminStaffDetail = z.infer<typeof adminStaffDetailSchema>;
export const adminStaffInputSchema = z.object({
  mutationId: z.uuid(),
  staffId: z.uuid(),
  displayName: z.string().trim().min(1).max(120),
  role: roleSchema,
  active: z.boolean(),
});
export type AdminStaffInput = z.infer<typeof adminStaffInputSchema>;
export const adminStaffCreateInputSchema = adminStaffInputSchema.omit({ staffId: true });
export type AdminStaffCreateInput = z.infer<typeof adminStaffCreateInputSchema>;
export const adminStaffPinInputSchema = z.object({
  mutationId: z.uuid(),
  staffId: z.uuid(),
  pin: z.string().regex(/^\d{4}$/),
});
export type AdminStaffPinInput = z.infer<typeof adminStaffPinInputSchema>;

export const adminDeviceHealthSchema = z.enum(['REVOKED', 'UNKNOWN', 'STALE', 'HEALTHY']);
export const adminDeviceSummarySchema = z.object({
  id: z.uuid(),
  friendlyName: z.string().min(1),
  active: z.boolean(),
  revokedAt: z.iso.datetime({ offset: true }).nullable(),
  bindingStatus: z.enum(['BOUND', 'UNBOUND']),
  healthStatus: adminDeviceHealthSchema,
  lastSeenAt: z.iso.datetime({ offset: true }).nullable(),
  lastSyncAt: z.iso.datetime({ offset: true }).nullable(),
  pendingCount: z.number().int().nonnegative().nullable(),
  appVersion: z.string().nullable(),
  protocolVersion: z.number().int().positive(),
  provisionedAt: z.iso.datetime({ offset: true }),
  enrollmentStatus: z.enum(['NONE', 'READY', 'EXPIRED', 'CONSUMED']),
});
export const adminDevicesPageSchema = z.object({
  items: z.array(adminDeviceSummarySchema),
  staleThresholdMinutes: z.number().int().positive(),
  generatedAt: z.iso.datetime({ offset: true }),
});
export type AdminDevicesPage = z.infer<typeof adminDevicesPageSchema>;
export const adminDeviceDetailSchema = adminDeviceSummarySchema.extend({
  recentReviewCount: z.number().int().nonnegative(),
  recentSales: z.number().int().nonnegative(),
  revokedOfflineLeases: z.number().int().nonnegative(),
});
export type AdminDeviceDetail = z.infer<typeof adminDeviceDetailSchema>;
export const adminDeviceFiltersSchema = z.object({
  search: z.string().trim().max(160).optional(),
  status: z.enum(['HEALTHY', 'STALE', 'REVOKED', 'UNKNOWN', 'UNBOUND']).optional(),
});
export type AdminDeviceFilters = z.infer<typeof adminDeviceFiltersSchema>;
export const adminDeviceEnrollmentSchema = z.object({
  status: z.literal('ACCEPTED'),
  mutationId: z.uuid(),
  deviceId: z.uuid(),
  enrollmentCode: z.string().length(10),
  expiresAt: z.iso.datetime({ offset: true }),
});
export type AdminDeviceEnrollment = z.infer<typeof adminDeviceEnrollmentSchema>;
